// Refactored by j-funk into functional component 2023/09
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { useMeasure } from 'react-use';

import d3 from '../../core/d3_3.5.17';
import { saveText, saveImage } from '../../core/save';

import Legend from '../Legend';
import GeneNetworkToolbar from '../GeneNetworkToolbar';

// eslint-disable-next-line css-modules/no-unused-class
import s from './GeneNetwork.css';

const d3Network = require('./d3.network.js');

const GeneNetwork = React.forwardRef((props, svgRef) => {
  const [network, setNetwork] = useState(null);
  const [containerRef, { width }] = useMeasure();
  const defaultWidth = 530;

  const currentWidth = width === 0 ? defaultWidth : width;

  const {
    degreeCorrect,
    edgeCutoff,
    edges,
    genes,
    geneSets,
    height,
    nodeLimit,
    onCreate,
    onGeneSetRemove,
    onNetworkFilter,
  } = props;

  const handleGeneClick = d => {
    if (d3.event.defaultPrevented || !network) return; // click suppressed, was a drag move
    network.stop();
    network.clearEdgeSelection();
    network.clearNodeSelection();
    network.selectNode(d);
    if (props.onNodeClick) props.onNodeClick(d);
  };

  const handleEdgeClick = d => {
    if (!network || !svgRef.current) return;
    const edges = network.getMatchingBindNetworksEdge(d);
    Object.keys(edges).forEach(id => {
      if (edges[id]) {
        network.bindNetworks()[id].clearEdgeSelection();
      }
    });
    network.clearNodeSelection();
    network.selectEdge(d);
    if (props.onEdgeClick) props.onEdgeClick(d, edges);
  };

  useEffect(() => {
    const newNetwork = d3Network(d3);
    setNetwork(() => newNetwork);
  }, []);

  useEffect(() => {
    if (!network || !svgRef.current) return;

    // Initialize d3 layout and draw
    network.genes(genes).edges(edges);

    network.onEdge('click', handleEdgeClick);
    network.onGene('click', handleGeneClick);

    if (onNetworkFilter) {
      network.on('genechange', (genesadd, genesrm, genes) =>
        onNetworkFilter(genesadd, genesrm, genes),
      );
    }

    d3.select(svgRef.current).call(network);
    network
      .geneSets(Object.keys(geneSets).map(key => geneSets[key]))
      .filter(edgeCutoff, nodeLimit)
      .degreeCorrect(degreeCorrect)
      .fixed(true)
      .draw();

    setTimeout(network.fitZoom, 1000);

    if (onCreate) onCreate(network);
  }, [network, svgRef]);

  useEffect(() => {
    if (!network || !svgRef.current) return;
    network.genes(genes).edges(edges);
    d3.select(svgRef.current).call(network);
  }, [edges, genes, network, svgRef]);

  useEffect(() => {
    if (!network || !svgRef.current) return;
    network.degreeCorrect(degreeCorrect);
  }, [degreeCorrect, network, svgRef]);

  useEffect(() => {
    if (!network || !svgRef.current) return;
    network.filter(edgeCutoff, nodeLimit).draw();
    setTimeout(network.fitZoom, 750);
  }, [edgeCutoff, network, nodeLimit, degreeCorrect, svgRef]);

  useEffect(() => {
    if (!network || !svgRef.current) return;
    network
      .geneSets(Object.keys(geneSets).map(key => geneSets[key]))
      .updateStyling();
  }, [geneSets, network, svgRef]);

  useEffect(() => {
    if (!network || !svgRef.current) return;
    network.width(currentWidth);
    network.draw();
    network.fitZoom();
  }, [currentWidth, network, svgRef]);

  return network && svgRef.current ? (
    <div className={s.root}>
      <GeneNetworkToolbar
        style={{
          left: 0,
          top: 0,
          width: currentWidth,
          position: 'absolute',
        }}
        geneSets={geneSets}
        onZoomOut={() => {
          network.scale(network.scale() * 0.9);
        }}
        onZoomIn={() => {
          network.scale(network.scale() * 1.1);
        }}
        onFitZoom={() => {
          network.fitZoom();
        }}
        onInnerLayout={(inner, outer) => {
          network.innerLayout(inner);
          network.outerLayout(outer);
          setTimeout(network.fitZoom, 750);
        }}
        onOuterLayout={(inner, outer) => {
          network.innerLayout(inner);
          network.outerLayout(outer);
          setTimeout(network.fitZoom, 750);
        }}
        onLock={locked => {
          network.fixed(locked);
        }}
        onGeneSetRemove={onGeneSetRemove}
        onExpand={expand => {
          props.onExpandView(expand);
        }}
        onSaveImage={() => {
          saveImage(network.image(), `${props.title.replace(' ', '_')}.png`);
        }}
        onSaveText={() => {
          saveText(network.text(), `${props.title.replace(' ', '_')}.txt`);
        }}
        onSaveTextFull={() => {
          saveText(network.text(true), `${props.title.replace(' ', '_')}.txt`);
        }}
      />
      <div ref={containerRef}>
        <svg height={height} width={currentWidth} ref={svgRef} />
      </div>
      {network && (
        <Legend color={network.edgeColor()} height={10} width={currentWidth} />
      )}
    </div>
  ) : (
    <div className={s.root}>
      <div ref={containerRef}>
        <svg height={height} width={currentWidth} ref={svgRef} />
      </div>
    </div>
  );
});

GeneNetwork.propTypes = {
  genes: PropTypes.array.isRequired,
  edges: PropTypes.array.isRequired,
  nodeLimit: PropTypes.number,
  edgeCutoff: PropTypes.number,
  degreeCorrect: PropTypes.bool,
  geneSets: PropTypes.object,
  height: PropTypes.number,
  title: PropTypes.string,
  onEdgeClick: PropTypes.func,
  onNodeClick: PropTypes.func,
  onNetworkFilter: PropTypes.func,
  onCreate: PropTypes.func,
  onGeneSetRemove: PropTypes.func,
  onExpandView: PropTypes.func,
};

GeneNetwork.defaultProps = {
  height: 600,
  title: 'network',
  degreeCorrect: false,
};

export default withStyles(s)(GeneNetwork);
